import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import TheEngine from "@/pages/TheEngine";
import Solutions from "@/pages/Solutions";
import Terminal from "@/pages/Terminal";
import Login from "@/pages/Login";
import ClientPortal from "@/pages/ClientPortal";
import ClientProject from "@/pages/ClientProject";
import AdminDashboard from "@/pages/AdminDashboard";

import SectionOverview from "@/pages/SectionOverview";
import SectionPage from "@/pages/SectionPage";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import CareersApply from "@/pages/CareersApply";
import Pricing from "@/pages/Pricing";
import ServicesOverview from "@/pages/ServicesOverview";
import ServicesDispatch from "@/pages/ServicesDispatch";
import GeoDominance from "@/pages/GeoDominance";
import Work from "@/pages/Work";
import PriorityPitch from "@/pages/PriorityPitch";
import SleepyOwlPitch from "@/pages/SleepyOwlPitch";
import ApexPitch from "@/pages/ApexPitch";
import AdvantagePitch from "@/pages/AdvantagePitch";
import EndexiaPitch from "@/pages/EndexiaPitch";
import StillPitch from "@/pages/StillPitch";
import AboutPage from "@/pages/AboutPage";
import ImpactLanding from "@/pages/ImpactLanding";
import LaunchLanding from "@/pages/LaunchLanding";
import DemoSite from "@/pages/DemoSite";
import AdminDemos from "@/pages/AdminDemos";
import MolliesDemo from "@/pages/MolliesDemo";
import MemphisSoldiersDemo from "@/pages/MemphisSoldiersDemo";
import MidsouthPuppyDemo from "@/pages/MidsouthPuppyDemo";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { ThemeProvider } from "@/components/ThemeContext";

function App() {
  return (
    <div className="App bg-[#0B0C10] min-h-screen text-[#F5F5F7]">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
          <Routes>
            {/* Standalone PIN-gated pitch vaults (no Layout chrome) */}
            <Route path="/priority" element={<PriorityPitch />} />
            <Route path="/sleepy-owl" element={<SleepyOwlPitch />} />
            <Route path="/apex" element={<ApexPitch />} />
            <Route path="/advantage" element={<AdvantagePitch />} />
            <Route path="/endexia" element={<EndexiaPitch />} />
            <Route path="/still" element={<StillPitch />} />

            {/* Feature-flagged sales landing pages (toggled via Admin → Site Toggles) */}
            <Route path="/impact" element={<ImpactLanding />} />
            <Route path="/launch" element={<LaunchLanding />} />

            {/* Bespoke prospect demos (static segments win over /demo/:slug) */}
            <Route path="/demo/mollies-creations" element={<MolliesDemo />} />
            <Route path="/demo/memphis-soldiers" element={<MemphisSoldiersDemo />} />
            <Route path="/demo/midsouth-puppy-delivery" element={<MidsouthPuppyDemo />} />

            {/* Standalone prospect demo sites (client-branded, no TraceElements chrome) */}
            <Route path="/demo/:slug" element={<DemoSite />} />

            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/the-engine" element={<TheEngine />} />
              <Route path="/solutions-legacy" element={<Solutions />} />
              <Route path="/terminal" element={<Terminal />} />
              <Route path="/services" element={<ServicesOverview />} />
              <Route path="/services/:slug" element={<ServicesDispatch />} />
              <Route path="/geo" element={<GeoDominance />} />
              <Route path="/work" element={<Work />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/retainers" element={<Pricing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />

              {/* Section overviews */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/solutions" element={<SectionOverview section="solutions" />} />
              <Route path="/industries" element={<SectionOverview section="industries" />} />
              <Route path="/careers" element={<SectionOverview section="careers" />} />

              {/* Careers apply — must be before /careers/:slug to win route ranking */}
              <Route path="/careers/apply" element={<CareersApply />} />

              {/* Section detail pages */}
              <Route path="/about/:slug" element={<SectionPage section="about" />} />
              <Route path="/solutions/:slug" element={<SectionPage section="solutions" />} />
              <Route path="/industries/:slug" element={<SectionPage section="industries" />} />
              <Route path="/careers/:slug" element={<SectionPage section="careers" />} />

              {/* Insights blog */}
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightDetail />} />

              <Route
                path="/portal"
                element={<ProtectedRoute role="client"><ClientPortal /></ProtectedRoute>}
              />
              <Route
                path="/portal/projects/:id"
                element={<ProtectedRoute role="client"><ClientProject /></ProtectedRoute>}
              />
              <Route
                path="/admin"
                element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>}
              />
              <Route
                path="/admin/demos"
                element={<ProtectedRoute role="admin"><AdminDemos /></ProtectedRoute>}
              />
            </Route>
          </Routes>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0B0C10",
            border: "1px solid #B91C2C",
            color: "#F5F5F7",
            borderRadius: 0,
            fontFamily: "'JetBrains Mono', monospace",
          },
        }}
      />
    </div>
  );
}

export default App;
